import React, { useState, useEffect, type JSX } from 'react';
import {
    Button,
    Card,
    Container,
    Grid,
    Typography,
    Stack,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    useMediaQuery,
    CardContent,
    CardActions,
    Box,
    Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { RefetchConfigOptions } from '@reduxjs/toolkit/dist/query/core/apiState';
import { useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

//components
import { Hierarchy } from '../../components/Hierarchy';
import {
    AddPredictPulseDevice,
    AutoCompleteTags,
    DiscardModal,
    ErrorModal,
    SubscriptionLimitModal,
    SubscriptionPlanModal,
} from './index';
import {
    BackdropLoader,
    CustomHeader,
    CustomTransComponent,
    // getHierarchyIcons,
    renderGroupHierarchyIcons,
} from '../../components';

import Element from '../../components/Element';
import { localizeAddDeviceElements } from '@fiji/common/src/features/deviceManagement/localizeAddDeviceElements';

import { handleWhiteSpaces, toFirstLetterUpperCase } from '../../utils/helpers';

import {
    DEVICE_FIELDS_LIMIT,
    TAGS_LIMIT,
    BLUEPRINT_ORGANIZATION_NAME,
    VENDOR_NAME,
    DEVICE_CATEGORY,
    MasterDataKey,
    GATEWAY_VENDOR,
    UPS_DEVICE_TYPE_KEY,
    ALLOWED_UPS_DEVICE_TYPES,
    isAddDeviceAllowedParentGateway,
} from '@fiji/common/src/constants';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import {
    useCreateTagMutation,
    useGetTagsListMutation,
    useGetTagsQuery,
} from '@fiji/common/src/features/TagManagement/tagManagementApi';
import {
    useCreateDeviceMutation,
    useGetMasterDataMutation,
    useGetModelAssemblyMutation,
    useGetUIMetadataMutation,
    useGenerateDeviceNameMutation,
    useGetDeviceByIdQuery,
    useGetDeviceModelCountStatusQuery,
} from '@fiji/common/src/features/deviceManagement/deviceApi';
import { useGetAllGroupsMutation, useGetGroupDetailsQuery } from '@fiji/common/src/features/group/groupApi';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { Device } from '@fiji/common/src/types/Device';

//styles
import useStyles from './styles';
import { useLocation } from 'react-router-dom';
import { TagType } from '@fiji/common/src/types';
import SuccessModal from './SuccessModal';
import { useNavigationBack, useNewCustomTranslations, useTextFieldError } from 'hooks';
//Constraint
import { metadata } from '@fiji/common/src/constants/validation';
import { useGetAllSubscriptionsQuery } from '@fiji/common/src/features/Subscriptions/subscriptionApi';
import { useGetAllSubscriptionsV2Query } from '@fiji/common/src/features/Subscriptions/subscriptionV2Api';
import { useEntitlementsV2Flag } from '@fiji/common/src/hooks';
import LanguageTextField from 'components/FormElements/LanguageTextField';
import { MachineComponentList } from 'components/MachineComponentList';
import { RemoveCircleOutline, SentimentSatisfiedAltOutlined } from '@mui/icons-material';

const initialDevicePayload = {
    description: '',
    deviceName: '',
    deviceType: '',
    groupId: '',
    tagIds: [],
    deviceModel: '',
    connectedGateway: '',
};

type DeviceType = {
    key: string;
    name?: string;
    value?: string;
    type?: string;
    vendor?: string;
    profileId?: string;
};

type DeviceModel = {
    key: string;
    value: string;
    profileId?: string;
    serial?: string;
    vendor?: string;
    type?: string;
};

/**
 * Change event for the device form's MUI inputs. MUI's `SelectChangeEvent`
 * carries `name` but not `id`, while native inputs may supply either, so both
 * are optional and the field key is resolved from whichever is present.
 */
type DeviceFieldChangeEvent = {
    target: { id?: string; name?: string; value: string };
};

/**
 * Matches the PredictPulse device type coming back from master data.
 *
 * Master data ships this type under the single label below, so it is compared
 * exactly (trimmed, case-insensitive) rather than pattern-matched. If a future
 * environment ships a different label, add it here -- a loose pattern match was
 * deliberately avoided so an unrelated device type can never be misdetected and
 * silently lose its form.
 */
const PREDICT_PULSE_DEVICE_TYPE_LABEL = 'ups - predict pulse';

const isPredictPulseDeviceType = (device?: DeviceType | null): boolean => {
    const { key, name, value } = device ?? {};

    return [key, name, value].some((field) => field?.trim().toLowerCase() === PREDICT_PULSE_DEVICE_TYPE_LABEL);
};

export const AddDevice = (): JSX.Element => {
    const { t } = useTranslation();
    useNewCustomTranslations('ADD_DEVICE_FORM');
    const theme: any = useTheme();
    const { state } = useLocation();
    const { deviceId } = useParams();
    const dispatch = useAppDispatch();
    const classes = useStyles(theme);
    const sm = useMediaQuery(theme.breakpoints.up('sm'));
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const currentOrg = useTypedSelector(selectedOrg);
    const navigationBack = useNavigationBack();
    const { hasTextError, makeErrorHandler } = useTextFieldError();

    const [devicePayload, setDevicePayload] = useState<Partial<Device>>({
        ...initialDevicePayload,
    });
    const [responseError, setResponseError] = useState<any[]>([]);
    const [errorSavingModal, setErrorSavingModal] = useState(false);
    const [discardChangesModal, setDiscardChangesModal] = useState(false);
    const [subscriptionLimitModal, setSubscriptionLimitModal] = useState(false);
    const [groupsData, setGroupsData] = React.useState<any>();
    const [subscriptionPlanModal, setSubscriptionPlanModal] = useState(false);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('');
    const [selectedDeviceLicenseId, setSelectedDeviceLicenseId] = useState('');
    const [isError, setIsError] = useState(false);
    const [successModal, setSuccessModal] = useState(false);
    const [saveType, setSaveType] = useState('');
    const [openSelectBox, setOpenSelectBox] = useState(false);
    const [openConnectedGateway, setOpenConnectedGateway] = useState(false);
    const [deviceModels, setDeviceModels] = React.useState<DeviceModel[]>([]);
    const [deviceModel, setDeviceModel] = useState<DeviceModel | null>(null);
    const [pagePayload, setPagePayload] = React.useState({
        page: 0,
        size: 10,
    });
    const [hierarchyMappingData, setHierarchyMappingData] = React.useState<{ [key: string]: any }>({});
    const [connectedGatewayData, setConnectedGatewayData] = React.useState<{ [key: string]: any }>({});
    const [groupDetails, setGroupDetails] = React.useState<any>({
        destinationGroup: '',
    });
    const [connectedGatewayGroup, setConnectedGatewayGroup] = React.useState<any>({
        id: '',
    });
    const [loadingHierarchyNode, setLoadingHierarchyNode] = React.useState([]);
    const [loadingConnectedGatewayNode, setLoadingConnectedGatewayNode] = React.useState([]);
    const [deviceTypes, setDeviceTypes] = useState([]);
    const [deviceType, setDeviceType] = useState<DeviceType | null>(null);
    const [elements, setElements] = React.useState<any[]>([]);
    const [DeviceNameValidation] = useState<any>(metadata.validation.addDeviceForm.DeviceName);
    const [TagNameValidation] = useState<any>(metadata.validation.addDeviceForm.Tags);
    const [isTagError, setIsTagError] = useState(false);
    const [machineComponents, setMachineComponents] = useState<any[]>([]);

    const [getHeirarchyData, { isLoading: hierarchyDataLoader, data: groupsHierarchy }] = useGetAllGroupsMutation({
        fixedCacheKey: 'cachedAllGroupData',
    });
    const { data: groupData } = useGetGroupDetailsQuery(state?.groupId, {
        skip: !state?.groupId,
    });
    const { data: deviceData } = useGetDeviceByIdQuery(state?.deviceId, {
        skip: !state?.deviceId,
    });

    const {
        data: deviceTagsList,
        isLoading: isDeviceTagLoading,
        isFetching: isDeviceTagFetching,
        refetch: refetchDeviceTags,
    } = useGetTagsQuery<{
        data: any;
        isLoading: boolean;
        isFetching: boolean;
        refetch: RefetchConfigOptions;
    }>(
        {
            page: 0,
            size: 100,
        },
        { skip: !currentOrg?.id }
    );
    const { data: allDeviceCountStatus } = useGetDeviceModelCountStatusQuery({
        vendorName: 'BLI Pre Production Test',
        customerName: 'sierra',
        key: 'SubscriptionDeviceCount',
    });

    const [createTag, { isLoading: isTagCreating }] = useCreateTagMutation();
    const [getTagsList, { isLoading: isTagsListLoading }]: any = useGetTagsListMutation();
    const [createDevice, { isLoading: isDeviceCreated, error: deviceError, data: deviceSuccess }]: any =
        useCreateDeviceMutation();

    const {
        handleSubmit,
        reset,
        control,
        formState: { isDirty, isValid },
        watch,
    } = useForm({ mode: 'onChange' });

    const [getMasterData, { isLoading: isLoadingMasterData }]: any = useGetMasterDataMutation();
    const [getModelAssembly, { isLoading: isLoadingModelAssembly }]: any = useGetModelAssemblyMutation();
    const [getUIMetadata, { isLoading: isLoadingUiMetadata }]: any = useGetUIMetadataMutation();
    const [generateDeviceName, { isLoading: isLoadingDeviceName }]: any = useGenerateDeviceNameMutation();

    const { realmPrefix } = useParams();
    const { isEntitlementsV2, isFlagLoading } = useEntitlementsV2Flag();
    const selectedDeviceTypeEntry = (deviceTypes as DeviceType[])?.find(
        (entry) => entry?.key === devicePayload.deviceType
    );
    const isPredictPulseType = isPredictPulseDeviceType(selectedDeviceTypeEntry);

    const {
        data: allSubscriptionsV1,
        isLoading: allSubscriptionsLoaderV1,
        isSuccess: isSuccessV1,
    } = useGetAllSubscriptionsQuery(
        {
            body: {
                ...pagePayload,

                filters: {
                    profileId: deviceModel?.profileId,
                },
            },
            headers: {
                'x-realm': realmPrefix,
                ...(state?.orgId && {
                    organizationId: state?.orgId,
                }),
            },
            params: {
                status: 'ACTIVE',
            },
        },
        { refetchOnMountOrArgChange: true, skip: !deviceModel || isFlagLoading || isEntitlementsV2 }
    );

    const {
        data: allSubscriptionsV2,
        isLoading: allSubscriptionsLoaderV2,
        isSuccess: isSuccessV2,
    } = useGetAllSubscriptionsV2Query(
        {
            body: {
                ...pagePayload,
                filters: {
                    profileId: deviceModel?.profileId,
                    status: ['active', 'deferred'],
                },
            },
            headers: {
                'x-realm': realmPrefix,
                ...(state?.orgId && {
                    organizationId: state?.orgId,
                }),
            },
        },
        { refetchOnMountOrArgChange: true, skip: !deviceModel || isFlagLoading || !isEntitlementsV2 }
    );

    const allSubscriptions = isEntitlementsV2 ? allSubscriptionsV2 : allSubscriptionsV1;
    const allSubscriptionsLoader = isEntitlementsV2 ? allSubscriptionsLoaderV2 : allSubscriptionsLoaderV1;

    const handlepagePayload = (page: any): void => {
        setPagePayload(page);
    };

    useEffect(() => {
        const getDeviceTypes = async (): Promise<void> => {
            const deviceTypesData = await getMasterData({
                vendorName: BLUEPRINT_ORGANIZATION_NAME,
                customerName: VENDOR_NAME,
                key: MasterDataKey.DEVICETYPES,
            });

            setDeviceTypes(deviceTypesData?.data?.data?.data?.value);
        };
        if (currentOrg?.id) {
            void getDeviceTypes();
        }
    }, [currentOrg]);

    useEffect(() => {
        if (currentOrg?.defaultGroupAccess) {
            setGroupDetails((prev: any) => {
                if (!prev.destinationGroup) {
                    return {
                        ...prev,
                        destinationGroup: currentOrg?.id,
                        destinationGroupName: `${currentOrg?.name} (Organization Root)`,
                        destinationGroupIcon: { web: { family: 'material-ui', name: 'CorporateFare' } },
                    };
                }
                return prev;
            });
        }
    }, [currentOrg]);

    useEffect(() => {
        if (state?.groupId && groupData && state?.type === 'group') {
            setGroupDetails((prev: any) => ({
                ...prev,
                destinationGroup: groupData?.data?.id,
                destinationGroupName: groupData?.data?.name,
                destinationGroupIcon: groupData?.data?.icon,
            }));
        }
    }, [state, groupData]);

    useEffect(() => {
        if (
            state?.deviceId &&
            deviceData &&
            state?.type === 'gateway' &&
            deviceData?.data?.vendorTypeName.toLowerCase() === GATEWAY_VENDOR.starfish
        ) {
            setConnectedGatewayGroup((prev: any) => ({
                ...prev,
                id: deviceData?.data?.id,
                name: deviceData?.data?.name,
                icon: deviceData?.data?.icon,
                vendorTypeName: deviceData?.data?.vendorTypeName,
            }));
        }
    }, [state, deviceData]);

    /* The below code is using the `useEffect` hook in a TypeScript React component. It is setting up a
    side effect that will be triggered whenever the `currentOrg` variable changes. */
    useEffect(() => {
        if (currentOrg?.id) {
            refetchDeviceTags()
                .then(() => {})
                .catch(() => {});
        }
    }, [currentOrg]);

    useEffect(() => {
        if (devicePayload.deviceModel && deviceModel?.type !== DEVICE_CATEGORY.child) {
            void fetchUIMetadata(deviceModel?.vendor || deviceType?.vendor || '');
        } else if (
            state?.type === 'gateway' &&
            deviceData?.data?.vendorTypeName.toLowerCase() === GATEWAY_VENDOR.starfish &&
            devicePayload.deviceModel &&
            devicePayload.deviceType
        ) {
            void fetchUIMetadata(connectedGatewayGroup?.vendorTypeName ?? '');
        }

        //machine gateway case — use modelassembly API instead of master data
        if (deviceModel?.vendor && deviceModel?.vendor.toLowerCase() === GATEWAY_VENDOR.machinegateway) {
            const profileId = deviceModel?.profileId;
            if (profileId) {
                setMachineComponents([]);
                getModelAssembly({ id: profileId }).then((res: any) => {
                    if (!res.error) {
                        setMachineComponents(res.data?.data?.data?.value ?? res.data?.data ?? []);
                    } else {
                        setMachineComponents([]);
                    }
                });
            }
        }
    }, [devicePayload.deviceModel]);

    const deviceCountConfig = React.useMemo(
        () => allDeviceCountStatus?.data?.data?.value?.find((item: any) => item.key === devicePayload?.deviceModel),
        [allDeviceCountStatus, devicePayload?.deviceModel]
    );
    const skipDeviceCount = allSubscriptions?.data?.records?.[0]?.skipDeviceCount;
    const isSubsriptionApiSuccess = isEntitlementsV2 ? isSuccessV2 : isSuccessV1;

    useEffect(() => {
        // No model selected means there is no subscription to be limited by, so any
        // previously raised limit state must be cleared -- otherwise the modal stays
        // open after the model is reset (e.g. switching to PredictPulse).
        if (!deviceModel) {
            setIsError(false);
            setSubscriptionLimitModal(false);
            return;
        }
        if (!allSubscriptionsLoader) {
            const records = allSubscriptions?.data?.records ?? [];
            const areSubscriptionLimitsReached =
                records.length > 0 &&
                records.every((subscription: any) =>
                    isEntitlementsV2
                        ? subscription.deviceLimit !== null && subscription.deviceLimit - subscription.deviceCount <= 0
                        : subscription.remainingQuantity === 0
                );

            if (areSubscriptionLimitsReached && deviceModel && !skipDeviceCount) {
                setIsError(true);
                setSubscriptionLimitModal(true);
            } else {
                setIsError(false);
                setSubscriptionLimitModal(false);
            }
        }
    }, [deviceModel, isSubsriptionApiSuccess, deviceCountConfig]);

    useEffect(() => {
        if (
            connectedGatewayGroup?.name !== '' &&
            devicePayload.deviceModel &&
            deviceModel?.type === DEVICE_CATEGORY.child
        ) {
            void fetchUIMetadata(connectedGatewayGroup?.vendorTypeName);
        }
    }, [connectedGatewayGroup]);

    /* The below code is using the useEffect hook in a React component. It is listening for changes in
    the variables `deviceError`, `deviceSuccess`, and `saveType`. */
    useEffect(() => {
        if (deviceError) {
            setErrorSavingModal(true);
            setResponseError(deviceError?.data?.errorMessage);
        }
        if (deviceSuccess) {
            if (
                deviceModel?.type === DEVICE_CATEGORY.gateway &&
                (deviceModel?.vendor?.toLowerCase() === GATEWAY_VENDOR.dashboard ||
                    deviceModel?.vendor?.toLowerCase() === GATEWAY_VENDOR.starfish ||
                    deviceModel?.vendor?.toLowerCase() === GATEWAY_VENDOR.generic ||
                    deviceModel?.vendor?.toLowerCase() === GATEWAY_VENDOR.machinegateway)
            ) {
                setSuccessModal(true);
            } else {
                dispatch(
                    setMessageContent({
                        isOpen: true,
                        message: t('ADD_DEVICE_FORM:DEVICE_SAVED_SUCCESSFULLY', {
                            replace: { deviceName: devicePayload.deviceName },
                        }),

                        duration: 3000,
                    })
                );

                if (saveType === 'saveAs') {
                    resetForm();
                } else {
                    navigationBack();
                }
            }
        }
    }, [deviceError, deviceSuccess]);

    useEffect(() => {
        reset();
        setElements([]);
        if (state?.type !== 'gateway') {
            setConnectedGatewayGroup({
                id: '',
                name: '',
                icon: '',
                vendorTypeName: '',
            });
        }
    }, [devicePayload.deviceType, devicePayload.deviceModel]);

    useEffect(() => {
        if (groupDetails.destinationGroup && devicePayload.deviceModel !== '') {
            const deviceDetails = getDeviceDetails();
            const getDeviceName = async (): Promise<void> => {
                const { data: deviceNameRes, error } = await generateDeviceName({
                    deviceTypeId: deviceDetails.profileId,
                    groupId: groupDetails.destinationGroup,
                });

                if (!error) {
                    setDevicePayload((prev) => ({ ...prev, deviceName: deviceNameRes.data.deviceName }));
                }
            };

            void getDeviceName();
        }
    }, [devicePayload.deviceType, groupDetails.destinationGroup, devicePayload.deviceModel]);

    const fetchUIMetadata = async (vendor: string): Promise<void> => {
        if (!vendor || !deviceModel?.type) return;
        const uiMetadataData = await getUIMetadata({
            vendorTypeName: vendor,
            deviceTypeName: toFirstLetterUpperCase(deviceModel?.type ?? ''),
        });
        setElements(uiMetadataData?.data?.data.data);
    };

    const getDeviceDetails = (): any => {
        let details = {};
        const deviceCategoryType = deviceModel?.type;
        if (deviceCategoryType === DEVICE_CATEGORY.gateway || deviceCategoryType === DEVICE_CATEGORY.standalone) {
            details = { vendor: deviceModel?.vendor || deviceType?.vendor, profileId: deviceModel?.profileId };
        } else if (deviceCategoryType === DEVICE_CATEGORY.child) {
            details = { vendor: connectedGatewayGroup?.vendorTypeName, profileId: deviceModel?.profileId };
        } else {
            details = {
                vendor: deviceModel?.vendor || deviceType?.vendor,
                profileId: deviceModel?.profileId,
            };
        }
        return details;
    };

    const handleFetchHierarchy = async (parentData: any): Promise<void> => {
        setLoadingHierarchyNode((prevState): any => [...prevState, parentData?.id]);

        const { data: childHierarchyData }: any = await getHeirarchyData({ parent: parentData?.id });

        if (childHierarchyData) {
            setHierarchyMappingData((prevState) => ({
                ...prevState,
                [parentData?.id]: childHierarchyData?.data?.records,
            }));
        }

        setLoadingHierarchyNode((prevState): any => prevState.filter((loadingNode) => loadingNode !== parentData?.id));
    };

    const handleCleanHierarchyNode = (parentId: string): void => {
        setHierarchyMappingData((prevState) => {
            const newHierarchyMappingData: any = { ...prevState };
            delete newHierarchyMappingData[parentId];
            return newHierarchyMappingData;
        });
    };

    const fetchConnectedGateway = async (parentData: any): Promise<void> => {
        setLoadingConnectedGatewayNode((prevState): any => [...prevState, parentData?.id]);

        const { data: childHierarchyData }: any = await getHeirarchyData({ parent: parentData?.id });

        if (childHierarchyData) {
            // FIJI-70579: Show only allowed parent gateway models (e.g. BLG300) in the picker.
            const allowedHierarchyRecords = (childHierarchyData?.data?.records ?? []).filter(
                (record: any) => record?.type !== 'GATEWAY' || isAddDeviceAllowedParentGateway(record?.model)
            );
            setConnectedGatewayData((prevState) => ({
                ...prevState,
                [parentData?.id]: allowedHierarchyRecords,
            }));
        }

        setLoadingConnectedGatewayNode((prevState): any =>
            prevState.filter((loadingNode) => loadingNode !== parentData?.id)
        );
    };

    const cleanConnectedGatewayNode = (parentId: string): void => {
        setConnectedGatewayData((prevState) => {
            const newHierarchyMappingData: any = { ...prevState };
            delete newHierarchyMappingData[parentId];
            return newHierarchyMappingData;
        });
    };

    const handleTreeSelection = (parentNode: any): void => {
        setConnectedGatewayGroup((prev: any) => ({
            ...prev,
            id: parentNode.id,
            name: parentNode.name,
            icon: parentNode.icon,
            vendorTypeName: parentNode.vendorTypeName,
        }));
        handleConnectedGatewaySelect();
    };

    /**
     * The `payloadHandler` function updates the `devicePayload` state based on the value of the target
     * element, and also triggers a debounce handler for specific target IDs.
     * @param {any} e - The parameter `e` is an event object that is passed to the `payloadHandler`
     * function. It contains information about the event that triggered the function, such as the
     * target element, the event type, and any additional data associated with the event.
     * @returns The function `payloadHandler` returns `void`, which means it does not return any value.
     */
    const payloadHandler = (e: any): void => {
        let key: string = e.target.id;

        if (e.target.value && key === 'deviceName') {
            // show error right away if it is regexp error
            const regexp = new RegExp(DeviceNameValidation.pattern);
            const testReturn = regexp.test(e.target.value);
            if (!testReturn) {
                return;
            }
        }

        if (e.target.name) {
            key = e.target.name;
        }
        if (e.target.value.length > DEVICE_FIELDS_LIMIT[key]) {
            return;
        }
        setDevicePayload((prev) => ({ ...prev, [key]: e.target.value }));
    };

    /**
     * Fetches the device models for a device type. UPS types additionally filter
     * the master-data response down to the models this form supports.
     */
    const fetchDeviceModelsForType = async (deviceTypeKey: string): Promise<DeviceModel[]> => {
        const deviceModelsData = await getMasterData({
            vendorName: BLUEPRINT_ORGANIZATION_NAME,
            customerName: VENDOR_NAME,
            key: deviceTypeKey,
        });

        const allDeviceModelsFromApi = deviceModelsData.data?.data?.data?.value;
        if (deviceTypeKey.toUpperCase() !== UPS_DEVICE_TYPE_KEY) {
            return allDeviceModelsFromApi ?? [];
        }
        return (
            allDeviceModelsFromApi?.filter((model: DeviceModel) =>
                ALLOWED_UPS_DEVICE_TYPES.includes(model.type ?? '')
            ) ?? []
        );
    };

    const handleDeviceType = async (e: DeviceFieldChangeEvent): Promise<void> => {
        const key = e.target.name || e.target.id || '';

        const selectedDeviceType: DeviceType | undefined = deviceTypes.find(
            (element: DeviceType) => element.key === e.target.value
        );
        setDeviceType(selectedDeviceType ?? null);
        setDevicePayload((prev) => ({ ...prev, [key]: e.target.value, deviceModel: '' }));
        // The model object drives the subscription queries and the limit-modal
        // effect. PredictPulse hides the model selector, so a stale model would
        // otherwise be unreachable yet still active.
        setDeviceModel(null);

        // PredictPulse shows activation-wizard instructions instead of a model
        // selector, so it has no device models to fetch.
        const needsDeviceModels = Boolean(selectedDeviceType) && !isPredictPulseDeviceType(selectedDeviceType);
        setDeviceModels(needsDeviceModels ? await fetchDeviceModelsForType(e.target.value) : []);
    };

    const handleDeviceModel = (e: any): void => {
        let key: string = e.target.id;
        if (e.target.name) {
            key = e.target.name;
        }
        const matchedDeviceModel: any = deviceModels.find((x: any) => x.key === e.target.value);
        if (matchedDeviceModel) {
            setDeviceModel(matchedDeviceModel);
        }
        setDevicePayload((prev) => ({ ...prev, [key]: e.target.value }));
    };

    /**
     * The function `selectedTagsHandler` updates the `tagIds` property in the `devicePayload` state
     * based on the selected tags.
     * @param {any} e - The parameter "e" is an event object that represents the event that triggered
     * the selectedTagsHandler function. It is typically used to access information about the event,
     * such as the target element or the event type.
     * @param {any} values - The `values` parameter is an array that contains the selected tags.
     * @returns If the length of `values` is greater than `TAGS_LIMIT`, nothing is being returned.
     * Otherwise, the `setDevicePayload` function is being called with an updated `tagIds` property in
     * the `prev` object.
     */
    const selectedTagsHandler = (e: any, values: any): void => {
        if (values?.length > TAGS_LIMIT) {
            return;
        }
        setDevicePayload((prev: any) => ({ ...prev, tagIds: values }));
    };

    /**
     * The function `handleKeyDown` is an asynchronous function that is triggered when a key is
     * pressed, and if the pressed key is the Enter key and certain conditions are met, it creates a
     * new device tag and updates the device payload.
     * @param {any} e - The parameter `e` is an event object that represents the keydown event. It
     * contains information about the event, such as the key code and target element.
     */
    const handleKeyDown = (e: any): void => {
        setIsTagError(false);
        // show error right away if it is regexp error
        const regexp = new RegExp(TagNameValidation.pattern);
        const testReturn = regexp.test(e.target.value);
        if (!testReturn) {
            setIsTagError(true);
        }

        if (e.code === 'Enter' && devicePayload?.tagIds && devicePayload?.tagIds?.length < TAGS_LIMIT && testReturn) {
            const tagValue = e.target.value;

            const devicePayloadClone = JSON.parse(JSON.stringify(devicePayload));
            devicePayloadClone.tagIds.push(tagValue);
            setDevicePayload(devicePayloadClone);
        }
    };

    const createDeviceTag = async (): Promise<void> => {
        let filteredTagsChosenIds = [];
        const allTagsDataNames = deviceTagsList?.data.records.map((item: TagType) => item.name);
        const newTagsToCreate = devicePayload.tagIds?.filter((x) => !allTagsDataNames.includes(x));

        if (newTagsToCreate?.length) {
            const createTagPromises = newTagsToCreate.map(
                async (tagName) =>
                    await createTag({
                        name: tagName,
                    })
            );
            await Promise.all(createTagPromises);

            const result = await getTagsList({
                page: 0,
                size: 100,
            });
            const resultTags = result?.data?.data?.records;
            filteredTagsChosenIds = resultTags
                ?.filter((x: { name: any }) => devicePayload.tagIds?.includes(x.name))
                .map((item: { id: any }) => item.id);
        } else {
            filteredTagsChosenIds = deviceTagsList?.data.records
                ?.filter((x: { name: any }) => devicePayload.tagIds?.includes(x.name))
                .map((item: { id: any }) => item.id);
        }
        return filteredTagsChosenIds;
    };

    const handleSubscriptionSelect = (data: string, deviceLicenseId?: string): void => {
        setSelectedSubscriptionId(data);
        setSelectedDeviceLicenseId(deviceLicenseId || '');
    };
    /**
     * The submitHandler function is used to handle form submission in a TypeScript React component,
     * updating or creating a device based on the form data.
     * @param {any} data - The parameter `data` is an data coming for dynamic form fields.
     * @param {any} e - The parameter `e` is an event object that is passed to the `submitHandler`
     * function. It is typically an object that contains information about the event that triggered the
     * function, such as the target element that was clicked or the form that was submitted. In this
     * case, it is being used to
     */
    const submitHandler = async (data: any, e: any): Promise<void> => {
        setSaveType(e.target.id);
        if (!allSubscriptionsLoader && allSubscriptions.data.records.length > 1) {
            setSubscriptionPlanModal(true);
        } else {
            setSaveType(e.target.id);
            const deviceDetails = getDeviceDetails();
            const deviceTags = await createDeviceTag();

            const modifiedData: any = getPayload(data);

            const payload = {
                deviceTypeName: toFirstLetterUpperCase(deviceModel?.type || ''),
                vendorTypeName: deviceDetails.vendor,
                payload: {
                    serial: deviceModel?.key, // for dashboard
                    requestedFrom: 'Remote', // for starfish
                    deviceProfileID: deviceDetails.profileId,
                    name: devicePayload.deviceName,
                    subscriptionId: allSubscriptions.data.records[0].id,
                    ...(isEntitlementsV2 && { deviceLicenseId: allSubscriptions.data.records[0].deviceLicenseId }),
                    groupId: groupDetails.destinationGroup,
                    tagId: deviceTags,
                    description: devicePayload.description,
                    ...(deviceModel?.type === DEVICE_CATEGORY.child && {
                        parentDeviceId: connectedGatewayGroup.id,
                        deviceType: deviceModel?.value,
                    }),
                    ...modifiedData,
                },
            };

            await createDevice(payload);
        }
    };
    const submitHandlerPlanModal = async (data: any): Promise<void> => {
        setSubscriptionPlanModal(false);

        const deviceDetails = getDeviceDetails();
        const deviceTags = await createDeviceTag();
        const modifiedData: any = getPayload(data);
        const payload = {
            deviceTypeName: toFirstLetterUpperCase(deviceModel?.type || ''),
            vendorTypeName: deviceDetails.vendor,
            payload: {
                serial: deviceModel?.key, // for dashboard
                requestedFrom: 'Remote', // for starfish
                deviceProfileID: deviceDetails.profileId,
                name: devicePayload.deviceName,
                subscriptionId: selectedSubscriptionId,
                ...(isEntitlementsV2 && selectedDeviceLicenseId && { deviceLicenseId: selectedDeviceLicenseId }),
                groupId: groupDetails.destinationGroup,
                tagId: deviceTags,
                description: devicePayload.description,
                ...(deviceModel?.type === DEVICE_CATEGORY.child && {
                    parentDeviceId: connectedGatewayGroup.id,
                    deviceType: deviceModel?.value,
                }),
                ...modifiedData,
            },
        };

        await createDevice(payload);
    };

    const getPayload = (data: any): any => {
        const modifiedData: any = {};

        elements.forEach((element) => {
            if (data.communicationType === element.show || !element.show) {
                modifiedData[element.id] = data[element.id];
            }
        });
        return modifiedData;
    };
    /**
     * The function `handleTagDelete` is used to remove a tag from an array of tag IDs in a device
     * payload.
     * @param {any} data - The `data` parameter is of type `any`, which means it can be any data type.
     * It is not specified what kind of data it represents in this context.
     * @param {number} index - The `index` parameter is the index of the element in the `tagIds` array
     * that needs to be deleted.
     */
    const handleTagDelete = (data: any, index: number): void => {
        const devicePayloadClone = JSON.parse(JSON.stringify(devicePayload));
        devicePayloadClone.tagIds.splice(index, 1);
        setDevicePayload(devicePayloadClone);
    };

    const isValidDeviceModel = (): boolean => {
        if (devicePayload.deviceModel === '') {
            return true;
        }
        return false;
    };

    const isValidConnectedGateway = (): boolean => {
        if (deviceModel?.type === DEVICE_CATEGORY.child && connectedGatewayGroup.id === '') {
            return true;
        }
        return false;
    };

    /**
     * The function `isValidCommonField` returns `true` if any of the specified conditions are met,
     * otherwise it returns `false`.
     * @returns The function `isValidCommonField` returns a boolean value.
     */
    const isValidCommonField = (): boolean => {
        if (
            devicePayload.deviceName === '' ||
            devicePayload.deviceType === '' ||
            groupDetails.destinationGroup === '' ||
            !handleWhiteSpaces([devicePayload.deviceName, devicePayload?.description])
        ) {
            return true;
        }
        return false;
    };

    const disableHandler = (): boolean =>
        isValidCommonField() || isValidDeviceModel() || isValidConnectedGateway() || !isValid || hasTextError;

    /**
     * The function `handleToggleSelectBox` toggles the state of `openSelectBox` between `true` and
     * `false`.
     */
    const handleToggleSelectBox = (): void => {
        setOpenSelectBox((prev) => {
            if (!prev === false) {
                setHierarchyMappingData({});
            }
            return !prev;
        });
    };
    const handleConnectedGatewaySelect = (): void => {
        setOpenConnectedGateway((prev) => {
            if (!prev === false) {
                setHierarchyMappingData({});
            }
            return !prev;
        });
    };

    /**
     * The function checks if the initial device payload is equal to the current device payload and
     * navigates to a different page if they are, otherwise it sets a discard changes modal to true.
     */
    const handleDiscardChanges = (): void => {
        if (JSON.stringify(initialDevicePayload) !== JSON.stringify(devicePayload) || isDirty) {
            setDiscardChangesModal(true);
        } else {
            navigationBack();
        }
    };

    const handlePredictPulseClose = (): void => {
        // Reuse the shared reset so react-hook-form state, dynamic elements and
        // machine components are cleared too — otherwise a form the user had
        // already dirtied stays dirty and wrongly triggers the discard modal.
        resetForm();
        setDeviceModels([]);
    };

    const handleOk = (): void => {
        setSuccessModal(false);

        if (saveType === 'saveAs') {
            resetForm();
        } else if (saveType === 'save') {
            navigationBack();
        }
    };

    const handleSubscription = (): void => {
        setSubscriptionLimitModal(false);
        setDeviceModel(null);

        setDevicePayload({
            ...initialDevicePayload,
            deviceType: devicePayload.deviceType,
        });
    };

    const resetForm = (): void => {
        setMachineComponents([]);
        setDeviceType(null);
        setDeviceModel(null);
        reset();
        setElements([]);
        setDevicePayload({
            ...initialDevicePayload,
        });
        setSuccessModal(false);
    };
    const isStarfishGateway = (): boolean => {
        if (deviceModel?.vendor) {
            return deviceModel?.vendor.toLowerCase() === GATEWAY_VENDOR.starfish;
        }
        return false;
    };

    const isMachineGateway = (): boolean => {
        if (deviceModel?.vendor) {
            return deviceModel?.vendor.toLowerCase() === GATEWAY_VENDOR.machinegateway;
        }
        return false;
    };

    useEffect(() => {
        if (!groupsData) {
            getHeirarchyData({}) as any;
        }
    }, []);

    useEffect(() => {
        if (!groupsData) {
            setGroupsData(groupsHierarchy?.data?.records);
        }
    }, [groupsHierarchy]);

    return (
        <React.Fragment>
            <header>
                <meta name="description" content={t('ADD_DEVICE_FORM.ADD_DEVICE_DESCRIPTION')} />
            </header>
            <Box
                sx={{
                    backgroundColor: theme.palette.background.paper,
                }}
                minHeight={'100vh'}
                position={'relative'}
            >
                <CustomHeader
                    title={<CustomTransComponent translationKey={'ROUTE_TITLES:ADD_DEVICE'} />}
                    subtitle={currentOrg?.name ?? ''}
                    handleNavigateBack={handleDiscardChanges}
                />
                <Container fixed className={classes.ContainerWrapper}>
                    <Card className={classes.card}>
                        <CardContent className={classes.cardBody}>
                            <Typography
                                variant={'h6'}
                                className={classes.sectionHeader}
                                data-testid="add-device-section-header"
                            >
                                <CustomTransComponent
                                    translationKey={'ADD_DEVICE_FORM:HEADER'}
                                    replace={{ orgName: currentOrg?.name ?? '' }}
                                />
                            </Typography>
                            <Typography
                                variant={'body2'}
                                className={classes.sectionHeader}
                                data-testid="mandatory-fields-note"
                            >
                                <CustomTransComponent translationKey={'ADD_DEVICE_FORM:INFORM_MANDATORY_FIELDS'} />
                            </Typography>
                            <FormControl variant={'filled'} className={classes.formControl} required fullWidth>
                                <InputLabel id={'select-device-type'} required={true} data-testid="device-type-label">
                                    <CustomTransComponent translationKey={'DEVICE_FORM_COMMON:DEVICE_TYPE'} />
                                </InputLabel>
                                <Select
                                    id="device-type"
                                    data-testid="DevicesType"
                                    disabled={isLoadingMasterData}
                                    labelId={'select-device-type'}
                                    value={devicePayload?.deviceType ?? ''}
                                    name="deviceType"
                                    onChange={(e): void => {
                                        void handleDeviceType(e);
                                    }}
                                >
                                    {deviceTypes?.map((device: DeviceType) => (
                                        <MenuItem
                                            key={`key${device.key}`}
                                            value={device?.key}
                                            data-testid={`device-type-option-${device?.key}`}
                                        >
                                            {device?.name ?? device?.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {isPredictPulseType && <AddPredictPulseDevice onClose={handlePredictPulseClose} />}

                            {!isPredictPulseType && devicePayload.deviceType && (
                                <>
                                    <FormControl variant={'filled'} className={classes.formControl} required fullWidth>
                                        <InputLabel
                                            id={'select-device-model'}
                                            required={true}
                                            data-testid="device-model-label"
                                        >
                                            <CustomTransComponent translationKey={'DEVICE_FORM_COMMON:DEVICE_MODEL'} />
                                        </InputLabel>
                                        <Select
                                            id="device-model"
                                            data-testid="device-model-select"
                                            disabled={isLoadingMasterData}
                                            labelId={'select-device-model'}
                                            value={devicePayload?.deviceModel ?? ''}
                                            name="deviceModel"
                                            onChange={(e): void => {
                                                handleDeviceModel(e);
                                            }}
                                        >
                                            {deviceModels?.map((device: DeviceModel) => (
                                                <MenuItem
                                                    key={`key${device.key}`}
                                                    value={device?.key}
                                                    data-testid={`device-model-option-${device?.key}`}
                                                >
                                                    {device?.value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {devicePayload?.deviceModel && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                sx={{
                                                    mt: 1,
                                                    px: 2,
                                                    py: 1,
                                                }}
                                            >
                                                {skipDeviceCount ? (
                                                    <SentimentSatisfiedAltOutlined
                                                        fontSize="small"
                                                        sx={{ color: '#007BC1' }}
                                                    />
                                                ) : (
                                                    <RemoveCircleOutline fontSize="small" sx={{ color: '#EC9917' }} />
                                                )}
                                                <Typography
                                                    variant="body2"
                                                    data-testid="device-count-message"
                                                    sx={{
                                                        fontFamily: 'Open Sans',
                                                        fontStyle: 'italic',
                                                        fontSize: 14,
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    <CustomTransComponent
                                                        translationKey={
                                                            skipDeviceCount
                                                                ? 'COMMON:DEVICE_COUNT_NOT_CONSUME_MSG'
                                                                : 'COMMON:DEVICE_COUNT_CONSUME_MSG'
                                                        }
                                                    />
                                                </Typography>
                                            </Stack>
                                        )}
                                    </FormControl>
                                    {isMachineGateway() &&
                                        devicePayload.deviceModel &&
                                        machineComponents.length > 0 && (
                                            <MachineComponentList
                                                header={devicePayload.deviceModel}
                                                items={machineComponents}
                                            />
                                        )}
                                    {deviceModel?.type !== DEVICE_CATEGORY.standalone && (
                                        <>
                                            {devicePayload.deviceModel &&
                                                deviceModel?.type === DEVICE_CATEGORY.child && (
                                                    <FormControl
                                                        variant={'filled'}
                                                        className={classes.formControl}
                                                        required
                                                        fullWidth
                                                    >
                                                        <InputLabel
                                                            id={'select-connected-gateway'}
                                                            required={false}
                                                            data-testid="connected-gateway-label"
                                                        >
                                                            <CustomTransComponent
                                                                translationKey={'EDIT_DEVICE_FORM:CONNECTED_GATEWAY'}
                                                            />
                                                        </InputLabel>
                                                        <Select
                                                            displayEmpty
                                                            id="connectedGateway"
                                                            data-testid="connected-gateway-select"
                                                            labelId="select-connected-gateway"
                                                            disabled={hierarchyDataLoader}
                                                            renderValue={(): JSX.Element => (
                                                                <Typography
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignContent: 'center',
                                                                        py: '8px',
                                                                    }}
                                                                >
                                                                    <div>
                                                                        {connectedGatewayGroup?.icon &&
                                                                            renderGroupHierarchyIcons({
                                                                                icon: connectedGatewayGroup?.icon,
                                                                            })}
                                                                    </div>
                                                                    <span>{connectedGatewayGroup?.name}</span>
                                                                </Typography>
                                                            )}
                                                            value={connectedGatewayGroup?.name ? ' ' : ''}
                                                            open={openConnectedGateway}
                                                            onClose={handleConnectedGatewaySelect}
                                                            onOpen={handleConnectedGatewaySelect}
                                                        >
                                                            <Hierarchy
                                                                labelKey="name"
                                                                data={[
                                                                    {
                                                                        id: currentOrg?.id,
                                                                        name: `${currentOrg?.name} (Organization Root)`,
                                                                        icon: {
                                                                            web: {
                                                                                family: 'material-ui',
                                                                                name: 'CorporateFare',
                                                                            },
                                                                        },
                                                                        groupCount: currentOrg?.groupCount,
                                                                        deviceCount: currentOrg?.deviceCount,
                                                                    },
                                                                ]}
                                                                loadMore={fetchConnectedGateway}
                                                                cleanHiearchyNode={cleanConnectedGatewayNode}
                                                                hierarchyMappingData={connectedGatewayData}
                                                                loadingNode={loadingConnectedGatewayNode}
                                                                selectionType={'radio'}
                                                                selectedNodes={connectedGatewayGroup?.id}
                                                                hasChild={(treeItem): boolean =>
                                                                    treeItem?.groupCount ||
                                                                    (treeItem?.deviceCount &&
                                                                        treeItem?.type !== 'GATEWAY')
                                                                }
                                                                getAvatarChild={renderGroupHierarchyIcons}
                                                                handleTreeNodeSelection={(parentNode): void => {
                                                                    handleTreeSelection(parentNode);
                                                                }}
                                                                filters={[
                                                                    {
                                                                        key: 'type',
                                                                        operator: '!==',
                                                                        value: 'GROUP',
                                                                        action: 'disableCheck',
                                                                    },
                                                                    {
                                                                        key: 'id',
                                                                        operator: '!==',
                                                                        value: currentOrg?.id,
                                                                        action: 'disableCheck',
                                                                    },
                                                                    {
                                                                        key: 'type',
                                                                        operator: '!==',
                                                                        value: 'DEVICE',
                                                                        action: 'hidden',
                                                                    },
                                                                    {
                                                                        key: 'type',
                                                                        operator: '!==',
                                                                        value: 'CHILD',
                                                                        action: 'hidden',
                                                                    },
                                                                    {
                                                                        key: 'vendorTypeName',
                                                                        operator: '!==',
                                                                        value: 'Dashboard',
                                                                        action: 'hidden',
                                                                    },
                                                                ]}
                                                            />
                                                        </Select>
                                                    </FormControl>
                                                )}
                                        </>
                                    )}

                                    {!isError && devicePayload.deviceModel && (
                                        <>
                                            <FormControl
                                                variant={'filled'}
                                                className={classes.formControl}
                                                required
                                                fullWidth
                                            >
                                                <InputLabel
                                                    id={'select-Assign-to-Group'}
                                                    required={false}
                                                    data-testid="assign-to-group-label"
                                                >
                                                    <CustomTransComponent translationKey={'COMMON:ASSIGN_TO_GRP'} />
                                                </InputLabel>
                                                <Select
                                                    displayEmpty
                                                    id="groupId"
                                                    data-testid="assign-to-group-select"
                                                    labelId="demo-customized-select-label"
                                                    disabled={hierarchyDataLoader}
                                                    renderValue={(): JSX.Element => (
                                                        <Typography
                                                            sx={{
                                                                display: 'flex',
                                                                alignContent: 'center',
                                                                py: '8px',
                                                            }}
                                                        >
                                                            <div>
                                                                {renderGroupHierarchyIcons({
                                                                    icon: groupDetails?.destinationGroupIcon,
                                                                })}
                                                            </div>
                                                            <span>{groupDetails?.destinationGroupName}</span>
                                                        </Typography>
                                                    )}
                                                    value={groupDetails?.destinationGroupName ? ' ' : ''}
                                                    open={openSelectBox}
                                                    onClose={handleToggleSelectBox}
                                                    onOpen={handleToggleSelectBox}
                                                >
                                                    <Hierarchy
                                                        labelKey="name"
                                                        isLoading={!groupsData}
                                                        data={
                                                            currentOrg?.defaultGroupAccess
                                                                ? [
                                                                      {
                                                                          id: currentOrg?.id,
                                                                          name: `${currentOrg?.name} (Organization Root)`,
                                                                          icon: {
                                                                              web: {
                                                                                  family: 'material-ui',
                                                                                  name: 'CorporateFare',
                                                                              },
                                                                          },
                                                                          groupCount: currentOrg?.groupCount,
                                                                          deviceCount: currentOrg?.deviceCount,
                                                                      },
                                                                  ]
                                                                : groupsData
                                                        }
                                                        loadMore={handleFetchHierarchy}
                                                        cleanHiearchyNode={handleCleanHierarchyNode}
                                                        hierarchyMappingData={hierarchyMappingData}
                                                        loadingNode={loadingHierarchyNode}
                                                        selectionType={'radio'}
                                                        selectedNodes={groupDetails?.destinationGroup}
                                                        hasChild={(treeItem): boolean => treeItem?.groupCount}
                                                        getAvatarChild={renderGroupHierarchyIcons}
                                                        handleTreeNodeSelection={(parentNode): void => {
                                                            setGroupDetails((prev: any) => ({
                                                                ...prev,
                                                                destinationGroup: parentNode.id,
                                                                destinationGroupName: parentNode.name,
                                                                destinationGroupIcon: parentNode.icon,
                                                            }));
                                                            handleToggleSelectBox();
                                                        }}
                                                        filters={[
                                                            {
                                                                key: 'type',
                                                                operator: '!==',
                                                                value: 'DEVICE',
                                                                action: 'hidden',
                                                            },
                                                            {
                                                                key: 'type',
                                                                operator: '!==',
                                                                value: 'GATEWAY',
                                                                action: 'hidden',
                                                            },
                                                            {
                                                                key: 'type',
                                                                operator: '!==',
                                                                value: 'CHILD',
                                                                action: 'hidden',
                                                            },
                                                        ]}
                                                    />
                                                </Select>
                                            </FormControl>

                                            <LanguageTextField
                                                className={classes.formControl}
                                                value={devicePayload?.deviceName}
                                                id="deviceName"
                                                data-testid="device-name-input"
                                                required
                                                label={
                                                    <CustomTransComponent
                                                        translationKey={'DEVICE_FORM_COMMON:DEVICE_NAME'}
                                                    />
                                                }
                                                fullWidth
                                                variant={'filled'}
                                                onChange={payloadHandler}
                                                handleTextError={makeErrorHandler('deviceName')}
                                                sx={{ marginBottom: '0 !important' }}
                                                inputProps={{
                                                    maxLength: DeviceNameValidation.maxLength,
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                data-testid="device-name-char-count"
                                                className="custom-add-device-field-lengths-style"
                                            >
                                                {`${devicePayload?.deviceName?.length}/${DEVICE_FIELDS_LIMIT['deviceName']}`}
                                            </Typography>

                                            {!isLoadingUiMetadata && elements
                                                ? localizeAddDeviceElements(elements, t).map(
                                                      (field: any) =>
                                                          (watch('communicationType') === field.show ||
                                                              !field.show) && (
                                                              <Element key={field.id} field={field} control={control} />
                                                          )
                                                  )
                                                : null}

                                            <Divider
                                                sx={{
                                                    mb: 3,
                                                    mx: 'auto',
                                                }}
                                            />

                                            <AutoCompleteTags
                                                error={isTagError}
                                                value={devicePayload?.tagIds}
                                                disabled={isDeviceTagFetching || isDeviceTagLoading}
                                                onChange={selectedTagsHandler}
                                                options={
                                                    deviceTagsList?.data.records.map(
                                                        (device: { name: string }) => device.name
                                                    ) ?? []
                                                }
                                                handleTagDelete={handleTagDelete}
                                                onKeyDown={handleKeyDown}
                                                placeholder={t('COMMON:TAGS')}
                                            />
                                            <Box className="modal-space-between">
                                                <Typography
                                                    variant="body2"
                                                    data-testid="tags-limit-text"
                                                    sx={{ paddingLeft: '16px' }}
                                                    className="custom-add-device-field-lengths-style"
                                                >
                                                    <CustomTransComponent
                                                        translationKey={'DEVICE_FORM_COMMON:TAGS_LIMIT_TEXT'}
                                                        replace={{ count: TAGS_LIMIT }}
                                                    />
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    data-testid="tags-count"
                                                    className="custom-add-device-field-lengths-style"
                                                >
                                                    {`${devicePayload?.tagIds?.length || 0}/${TAGS_LIMIT}`}
                                                </Typography>
                                            </Box>
                                            <LanguageTextField
                                                value={devicePayload?.description}
                                                id="description"
                                                data-testid="device-description-input"
                                                label={<CustomTransComponent translationKey={'COMMON:DESCRIPTION'} />}
                                                fullWidth
                                                variant={'filled'}
                                                onChange={payloadHandler}
                                                handleTextError={makeErrorHandler('description')}
                                                sx={{ marginBottom: '0 !important' }}
                                            />
                                            <Typography
                                                variant="body2"
                                                data-testid="description-char-count"
                                                sx={{ marginBottom: '0 !important' }}
                                                className="custom-add-device-field-lengths-style"
                                            >
                                                {`${devicePayload?.description?.length}/${DEVICE_FIELDS_LIMIT['description']}`}
                                            </Typography>
                                        </>
                                    )}
                                </>
                            )}
                        </CardContent>
                        {!isPredictPulseType && (
                            <CardActions className={classes.cardFooter}>
                                {sm ? null : (
                                    <Stack spacing={2} direction={'column'} sx={{ width: '100%' }}>
                                        <Button
                                            id="save"
                                            sx={{
                                                backgroundColor: theme?.palette?.primary?.main,
                                                '&:hover': {
                                                    backgroundColor: theme?.palette?.primary?.main,
                                                },
                                                '&.Mui-disabled': {
                                                    color: theme?.palette?.primary?.[200],
                                                    backgroundColor: theme?.palette?.primary?.[50],
                                                },
                                            }}
                                            variant={'contained'}
                                            onClick={(e): void => {
                                                handleSubmit(submitHandler)(e) as any;
                                            }}
                                            data-test="save-device-btn"
                                            data-testid="save-device-btn-mobile"
                                        >
                                            {<CustomTransComponent translationKey={'ADD_DEVICE_FORM:SAVE_DEVICE'} />}
                                        </Button>
                                        <Button
                                            id="saveAs"
                                            sx={{
                                                border: `1px solid ${theme?.palette?.primary?.main}`,
                                                '&:hover': {
                                                    backgroundColor: theme?.palette?.primary?.[50],
                                                    color: theme?.palette?.primary?.main,
                                                },
                                                '&.Mui-disabled': {
                                                    border: `1px solid ${theme?.palette?.primary?.[200]}`,
                                                    color: theme?.palette?.primary?.[200],
                                                },
                                            }}
                                            variant={'outlined'}
                                            data-test="save-and-add-new-device-btn"
                                            data-testid="save-and-add-new-device-btn-mobile"
                                        >
                                            {
                                                <CustomTransComponent
                                                    translationKey={'ADD_DEVICE_FORM:SAVE_AND_ADD_NEW_DEVICE'}
                                                />
                                            }
                                        </Button>
                                        <Button
                                            sx={{
                                                border: `1px solid ${theme?.palette?.primary?.main}`,
                                                '&:hover': {
                                                    backgroundColor: theme?.palette?.primary?.[50],
                                                    color: theme?.palette?.primary?.main,
                                                },
                                                '&.Mui-disabled': {
                                                    border: `1px solid ${theme?.palette?.primary?.[200]}`,
                                                    color: theme?.palette?.primary?.[200],
                                                },
                                            }}
                                            variant={'outlined'}
                                            onClick={handleDiscardChanges}
                                            data-test="cancel-btn"
                                            data-testid="cancel-btn-mobile"
                                        >
                                            <CustomTransComponent translationKey={'COMMON:CANCEL'} />
                                        </Button>
                                    </Stack>
                                )}
                                {smDown ? null : (
                                    <Grid container sx={{ width: '100%' }}>
                                        <Grid size={3}>
                                            <Button
                                                sx={{
                                                    border: `1px solid ${theme?.palette?.primary?.main}`,
                                                    '&:hover': {
                                                        backgroundColor: theme?.palette?.primary?.[50],
                                                        color: theme?.palette?.primary?.main,
                                                    },
                                                    '&.Mui-disabled': {
                                                        border: `1px solid ${theme?.palette?.primary?.[200]}`,
                                                        color: theme?.palette?.primary?.[200],
                                                    },
                                                }}
                                                variant={'outlined'}
                                                onClick={handleDiscardChanges}
                                                data-test="cancel-btn"
                                                data-testid="cancel-btn-desktop"
                                                id="cancel"
                                            >
                                                <CustomTransComponent translationKey={'COMMON:CANCEL'} />
                                            </Button>
                                        </Grid>
                                        <Grid size={9}>
                                            <Stack spacing={2} direction={'row'} justifyContent={'flex-end'}>
                                                {!deviceId && (
                                                    <Button
                                                        sx={{
                                                            border: `1px solid ${theme?.palette?.primary?.main}`,
                                                            '&:hover': {
                                                                backgroundColor: theme?.palette?.primary?.[50],
                                                                color: theme?.palette?.primary?.main,
                                                            },
                                                            '&.Mui-disabled': {
                                                                border: `1px solid ${theme?.palette?.primary?.[200]}`,
                                                                color: theme?.palette?.primary?.[200],
                                                            },
                                                        }}
                                                        variant={'outlined'}
                                                        disabled={disableHandler()}
                                                        id="saveAs"
                                                        onClick={(e): void => {
                                                            handleSubmit(submitHandler)(e) as any;
                                                        }}
                                                        data-test="save-and-add-new-device-btn"
                                                        data-testid="save-and-add-new-device-btn-desktop"
                                                    >
                                                        <CustomTransComponent
                                                            translationKey={'ADD_DEVICE_FORM:SAVE_AND_ADD_NEW_DEVICE'}
                                                        />
                                                    </Button>
                                                )}

                                                <Button
                                                    sx={{
                                                        backgroundColor: theme?.palette?.primary?.main,
                                                        '&:hover': {
                                                            backgroundColor: theme?.palette?.primary?.main,
                                                        },
                                                        '&.Mui-disabled': {
                                                            color: theme?.palette?.primary?.[200],
                                                            backgroundColor: theme?.palette?.primary?.[50],
                                                        },
                                                    }}
                                                    id="save"
                                                    variant={'contained'}
                                                    disabled={disableHandler()}
                                                    onClick={(e): void => {
                                                        handleSubmit(submitHandler)(e) as any;
                                                    }}
                                                    data-test="save-device-btn"
                                                    data-testid="save-device-btn-desktop"
                                                >
                                                    <CustomTransComponent
                                                        translationKey={'ADD_DEVICE_FORM:SAVE_DEVICE'}
                                                    />
                                                </Button>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                )}
                            </CardActions>
                        )}
                    </Card>
                </Container>
                <ErrorModal
                    key="errorModal"
                    retryHandler={(e): any => {
                        setErrorSavingModal(false);
                        handleSubmit(submitHandler)(e) as any;
                    }}
                    isVisible={errorSavingModal}
                    messages={responseError}
                    modalHandler={(action: boolean): void => setErrorSavingModal(action)}
                    clearPayload={(): void => setDevicePayload(initialDevicePayload)}
                    deviceId={deviceId}
                />
                <DiscardModal
                    key="discardModal"
                    isVisible={discardChangesModal}
                    modalHandler={(action: boolean): void => setDiscardChangesModal(action)}
                />
                <SuccessModal
                    key="successModal"
                    isVisible={successModal}
                    modalHandler={(action: boolean): void => setSuccessModal(action)}
                    isGenericModal={isStarfishGateway()}
                    connectionString={deviceSuccess?.data?.connectionString}
                    guid={deviceSuccess?.data?.deviceId}
                    okHandler={(): void => {
                        handleOk();
                    }}
                />

                <SubscriptionLimitModal
                    key="subscriptionLimit"
                    isVisible={subscriptionLimitModal}
                    modalHandler={(action: boolean): void => setSubscriptionLimitModal(action)}
                    deviceModal={devicePayload.deviceModel}
                    okHandler={(): void => {
                        handleSubscription();
                    }}
                />
                <SubscriptionPlanModal
                    key="subscriptionPlan"
                    isVisible={subscriptionPlanModal ?? false}
                    handlepagePayload={handlepagePayload}
                    modalHandler={(action: boolean): void => setSubscriptionPlanModal(action)}
                    isLoading={allSubscriptionsLoader}
                    subscriptionPlan={allSubscriptions?.data?.records}
                    total={allSubscriptions?.data?.total}
                    handleSubscriptionSelect={handleSubscriptionSelect}
                    okHandler={(e): any => {
                        handleSubmit(submitHandlerPlanModal)(e) as any;
                    }}
                    deviceCountConfig={deviceCountConfig}
                    deviceModel={deviceModel}
                />
                {(isLoadingUiMetadata ||
                    isLoadingMasterData ||
                    isLoadingModelAssembly ||
                    isDeviceCreated ||
                    isLoadingDeviceName ||
                    isDeviceTagLoading ||
                    isTagCreating ||
                    isTagsListLoading) && <BackdropLoader isOpen={true} />}
            </Box>
        </React.Fragment>
    );
};
